<template>
  <v-card style="min-height: 100vh; background: #212121" color="transparent" dark elevation="0" class="mx-auto neutral-dashboard">
    <!-- Loading Overlay -->
    <v-overlay :value="loading" absolute z-index="10" opacity="0.8" color="#212121">
      <v-col class="text-center">
        <v-progress-circular indeterminate size="64" width="6" color="white" class="mb-4"></v-progress-circular>
        <div class="text-h6 font-weight-light grey--text text--lighten-1">{{ loadingMessage }}</div>
      </v-col>
    </v-overlay>

    <!-- Fullscreen Chart Modal -->
    <v-dialog v-model="fullscreenChart.dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card color="#212121" dark class="d-flex flex-column">
        <v-toolbar color="#2E2E2E" dark dense flat class="flex-grow-0 pt-2 elevation-2">
          <v-toolbar-title class="text-h6 font-weight-medium pl-4">
            {{ fullscreenChart.title }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="fullscreenChart.dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="flex-grow-1 d-flex align-center justify-center pa-4" style="background: #212121">
          <div style="width: 100%; height: 85vh" v-if="fullscreenChart.dialog">
            <apexchart
              :type="fullscreenChart.type || 'bar'"
              width="100%"
              height="100%"
              :options="fullscreenChart.options"
              :series="fullscreenChart.series"></apexchart>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ✨ MODAL: Neuverträge Details ✨ -->
    <v-dialog v-model="showContractsDialog" max-width="500">
      <v-card color="#2E2E2E" dark class="rounded-xl">
        <v-card-title class="d-flex align-center border-bottom pa-4">
          <span class="text-h6 font-weight-bold">Neuverträge Details</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="showContractsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-list color="transparent" dense>
            <v-list-item v-for="(item, index) in newContractsDetailedList" :key="index" class="px-4 py-2">
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{ item.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip small label class="font-weight-bold">{{ item.value }}</v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ✨ MODAL: Wachstums Rangliste ✨ -->
    <v-dialog v-model="showRankingDialog" max-width="600">
      <v-card color="#2E2E2E" dark class="rounded-xl">
        <v-card-title class="d-flex align-center border-bottom pa-4">
          <span class="text-h6 font-weight-bold">Wachstums Rangliste</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="showRankingDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-simple-table class="transparent-table" dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left white--text">Rang</th>
                  <th class="text-left white--text">Studio</th>
                  <th class="text-right white--text">Zielerreichung</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in rankingList" :key="item.studioName">
                  <td>
                    <v-avatar
                      size="24"
                      :color="index === 0 ? 'amber' : index === 1 ? 'grey lighten-1' : index === 2 ? 'brown lighten-1' : 'transparent'"
                      class="mr-2">
                      <span class="white--text text-caption font-weight-bold" :class="index < 3 ? 'black--text' : ''">{{ index + 1 }}</span>
                    </v-avatar>
                  </td>
                  <td class="font-weight-medium">{{ item.studioName }}</td>
                  <td
                    class="text-right font-weight-bold"
                    :class="parseFloat(item.growthPercentage) >= 100 ? 'green--text' : 'grey--text text--lighten-1'">
                    {{ item.growthPercentage }}%
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Top Filter Section -->
    <v-card class="mb-8 pa-6 rounded-xl dashboard-card elevation-4">
      <v-row align="center" justify="center">
        <v-col cols="12" md="5">
          <v-menu v-model="menuStart" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedStartDate"
                label="Start Phase"
                prepend-inner-icon="mdi-calendar-range"
                readonly
                v-bind="attrs"
                v-on="on"
                outlined
                dense
                class="neutral-input"
                hide-details></v-text-field>
            </template>
            <v-date-picker v-model="startDate" @input="menuStart = false" :max="today" color="grey darken-3" no-title></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="5">
          <v-menu v-model="menuEnd" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedEndDate"
                label="End Phase"
                prepend-inner-icon="mdi-calendar-check"
                readonly
                v-bind="attrs"
                v-on="on"
                outlined
                dense
                class="neutral-input"
                hide-details></v-text-field>
            </template>
            <v-date-picker v-model="endDate" @input="menuEnd = false" :max="today" color="grey darken-3" no-title></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="2" class="text-center">
          <v-btn color="white" @click="fetchGrowthData" :loading="loading" large block class="black--text font-weight-bold rounded-lg">
            <v-icon left>mdi-refresh</v-icon>
            Update
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <div v-if="loaded">
      <!-- ✨ HIGHLIGHT CARDS (Quick Stats) ✨ -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card @click="showContractsDialog = true" v-ripple class="rounded-xl pa-4 gradient-card-1 elevation-6 card-hover cursor-pointer">
            <div class="text-overline white--text opacity-70">Neuverträge Total</div>
            <div class="text-h4 font-weight-bold white--text mt-1">
              {{ summaryStats.totalNewContracts }}
              <v-icon color="white" large class="float-right opacity-50">mdi-file-document-edit-outline</v-icon>
            </div>
            <div class="text-caption white--text mt-2 opacity-70">
              <v-icon small color="white" class="mr-1 opacity-70">mdi-cursor-default-click</v-icon>
              Klicken für Details
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-xl pa-4 gradient-card-2 elevation-6 card-hover">
            <div class="text-overline white--text opacity-70">Zielerreichung</div>
            <div class="text-h4 font-weight-bold white--text mt-1">
              {{ summaryStats.growthPercentage }}%
              <v-icon color="white" large class="float-right opacity-50">mdi-target</v-icon>
            </div>
            <div class="text-caption white--text mt-2 opacity-70">Wachstum vs. Ziel</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-xl pa-4 gradient-card-3 elevation-6 card-hover">
            <div class="text-overline white--text opacity-70">Zusatzpakete</div>
            <div class="text-h4 font-weight-bold white--text mt-1">
              {{ summaryStats.totalPackages }}
              <v-icon color="white" large class="float-right opacity-50">mdi-package-variant-closed</v-icon>
            </div>
            <div class="text-caption white--text mt-2 opacity-70">Verkauft (Neu & Upgrade)</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card @click="showRankingDialog = true" v-ripple class="rounded-xl pa-4 gradient-card-4 elevation-6 card-hover cursor-pointer">
            <div class="text-overline white--text opacity-70">Top Studio</div>
            <div class="text-h5 font-weight-bold white--text mt-2 text-truncate">
              {{ summaryStats.topStudio.name }}
              <span v-if="summaryStats.topStudio.growth" class="text-h6 opacity-80 ml-1">({{ summaryStats.topStudio.growth }}%)</span>
            </div>
            <v-icon color="white" large class="float-right opacity-50" style="margin-top: -30px">mdi-trophy-outline</v-icon>
            <div class="text-caption white--text mt-2 opacity-70">
              <v-icon small color="white" class="mr-1 opacity-70">mdi-format-list-numbered</v-icon>
              Klicken für Rangliste
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Wachstumsverlauf -->
      <v-col cols="12">
        <v-card class="rounded-xl dashboard-card elevation-4 mb-6" hover dark>
          <v-card-title class="d-flex align-center py-4 px-6 border-bottom">
            <div class="d-flex flex-column">
              <span class="text-h6 font-weight-bold">Wachstumsverlauf</span>
              <span class="text-caption grey--text">Entwicklung vs. Zielsetzung</span>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon small color="grey lighten-1" @click="openFullscreen('Wachstumsverlauf', growthChartOptions, growthSeries, 'line')">
              <v-icon>mdi-fullscreen</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-6 px-6">
            <v-alert dense border="left" color="blue-grey lighten-2" class="mb-6 rounded-lg" style="background: rgba(144, 164, 174, 0.1)">
              <div class="text-caption white--text">
                Hinweis: Kommen neue Center dazu, machen die Abozahlen in diesem Monat einen Sprung – das Wachstumsziel wirkt dadurch übertroffen.
              </div>
            </v-alert>
            <apexchart type="line" height="380" :options="growthChartOptions" :series="growthSeries"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Kunden pro QM & 100% Row -->
      <v-row>
        <v-col cols="12" md="6">
          <v-card class="rounded-xl dashboard-card elevation-4 fill-height" hover dark>
            <v-card-title class="d-flex align-center px-6 border-bottom">
              <span class="text-h6 font-weight-bold">Kunden pro QM</span>
              <v-spacer></v-spacer>
              <v-btn-toggle
                v-model="viewModes.customersPerQm"
                mandatory
                dense
                borderless
                background-color="rgba(255,255,255,0.05)"
                class="rounded-lg mr-2">
                <v-btn icon value="chart" small><v-icon size="18">mdi-chart-bar</v-icon></v-btn>
                <v-btn icon value="table" small><v-icon size="18">mdi-table</v-icon></v-btn>
              </v-btn-toggle>
              <v-btn
                icon
                small
                color="grey lighten-1"
                @click="openFullscreen('Kunden pro QM', customersPerQmChartOptions, customersPerQmSeries, 'bar')">
                <v-icon>mdi-fullscreen</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-6">
              <apexchart
                v-if="viewModes.customersPerQm === 'chart'"
                type="bar"
                height="320"
                :options="customersPerQmChartOptions"
                :series="customersPerQmSeries"></apexchart>
              <v-data-table
                v-else
                dense
                :items-per-page="-1"
                :headers="tableDataFor.customersPerQm.headers"
                :items="tableDataFor.customersPerQm.items"
                class="elevation-0 transparent neutral-table"
                dark></v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="rounded-xl dashboard-card elevation-4 fill-height" hover dark>
            <v-card-title class="d-flex align-center px-6 border-bottom">
              <span class="text-h6 font-weight-bold">Kunden pro 100%</span>
              <v-spacer></v-spacer>
              <v-btn-toggle
                v-model="viewModes.customersPer100"
                mandatory
                dense
                borderless
                background-color="rgba(255,255,255,0.05)"
                class="rounded-lg mr-2">
                <v-btn icon value="chart" small><v-icon size="18">mdi-chart-bar</v-icon></v-btn>
                <v-btn icon value="table" small><v-icon size="18">mdi-table</v-icon></v-btn>
              </v-btn-toggle>
              <v-btn
                icon
                small
                color="grey lighten-1"
                @click="openFullscreen('Kunden pro 100%', customersPer100ChartOptions, customersPer100Series, 'bar')">
                <v-icon>mdi-fullscreen</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-6">
              <apexchart
                v-if="viewModes.customersPer100 === 'chart'"
                type="bar"
                height="320"
                :options="customersPer100ChartOptions"
                :series="customersPer100Series"></apexchart>
              <v-data-table
                v-else
                dense
                :items-per-page="-1"
                :headers="tableDataFor.customersPer100.headers"
                :items="tableDataFor.customersPer100.items"
                class="elevation-0 transparent neutral-table"
                dark></v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Zusatzinfos Table -->
      <v-col cols="12">
        <v-card class="dashboard-card rounded-xl pa-2 elevation-4 mt-4">
          <v-toolbar flat color="transparent" dense class="px-2">
            <v-icon color="grey lighten-1" class="mr-2">mdi-table-large</v-icon>
            <v-toolbar-title class="subtitle-1 font-weight-bold">Erweiterte Datenmatrix</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-switch dense v-model="showCustomerInfosTable" inset color="white" hide-details class="mt-0"></v-switch>
          </v-toolbar>
          <v-expand-transition>
            <div v-show="showCustomerInfosTable">
              <v-divider class="mx-4"></v-divider>
              <v-data-table dense :items-per-page="-1" :headers="headers" :items="tableData" class="elevation-0 transparent pa-4 neutral-table" dark>
              </v-data-table>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>

      <!-- Section Header -->
      <v-col cols="12" class="mt-10 mb-4">
        <div class="d-flex align-center">
          <div class="text-h5 font-weight-bold mr-4 white--text">Vertragsanalyse</div>
          <v-divider class="grey darken-3"></v-divider>
        </div>
      </v-col>

      <!-- Neuverträge -->
      <v-col cols="12">
        <v-card class="rounded-xl dashboard-card elevation-4" hover dark>
          <v-card-title class="d-flex align-center px-6 border-bottom">
            <span class="text-h6 font-weight-bold">Neuverträge</span>
            <v-chip small label color="rgba(255,255,255,0.1)" class="ml-3 text-caption">Neumitglied & Ehemalig</v-chip>
            <v-spacer></v-spacer>
            <v-btn-toggle
              v-model="viewModes.newContracts"
              mandatory
              dense
              borderless
              background-color="rgba(255,255,255,0.05)"
              class="rounded-lg mr-2">
              <v-btn icon value="chart" small><v-icon size="18">mdi-chart-bar</v-icon></v-btn>
              <v-btn icon value="table" small><v-icon size="18">mdi-table</v-icon></v-btn>
            </v-btn-toggle>
            <v-btn icon small color="grey lighten-1" @click="openFullscreen('Neuverträge', newContractsChartOptions, newContractsSeries, 'bar')">
              <v-icon>mdi-fullscreen</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-6">
            <apexchart
              v-if="viewModes.newContracts === 'chart'"
              type="bar"
              height="350"
              :options="newContractsChartOptions"
              :series="newContractsSeries"></apexchart>
            <v-data-table
              v-else
              dense
              :items-per-page="-1"
              :headers="[
                { text: 'Studio', value: 'name' },
                { text: 'Anzahl', value: 'value' },
              ]"
              :items="tableDataFor.newContracts"
              class="elevation-0 transparent neutral-table"
              dark></v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Upgrades -->
      <v-col cols="12">
        <v-card class="rounded-xl dashboard-card elevation-4" hover dark>
          <v-card-title class="d-flex align-center px-6 border-bottom">
            <span class="text-h6 font-weight-bold">Vertrags-Upgrades</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewModes.upgrades" mandatory dense borderless background-color="rgba(255,255,255,0.05)" class="rounded-lg mr-2">
              <v-btn icon value="chart" small><v-icon size="18">mdi-chart-bar</v-icon></v-btn>
              <v-btn icon value="table" small><v-icon size="18">mdi-table</v-icon></v-btn>
            </v-btn-toggle>
            <v-btn icon small color="grey lighten-1" @click="openFullscreen('Vertrags-Upgrades', upgradesChartOptions, upgradesSeries, 'bar')">
              <v-icon>mdi-fullscreen</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-6">
            <apexchart
              v-if="viewModes.upgrades === 'chart'"
              type="bar"
              height="350"
              :options="upgradesChartOptions"
              :series="upgradesSeries"></apexchart>
            <v-data-table
              v-else
              dense
              :items-per-page="-1"
              :headers="[
                { text: 'Studio', value: 'name' },
                { text: 'Anzahl', value: 'value' },
              ]"
              :items="tableDataFor.upgrades"
              class="elevation-0 transparent neutral-table"
              dark></v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Zusatzpakete Verlauf -->
      <v-col cols="12">
        <v-card class="rounded-xl dashboard-card elevation-4" hover dark>
          <v-card-title class="d-flex align-center px-6 border-bottom">
            <span class="text-h6 font-weight-bold">Verkaufte Zusatzpakete (Verlauf)</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewModes.packages" mandatory dense borderless background-color="rgba(255,255,255,0.05)" class="rounded-lg mr-2">
              <v-btn icon value="chart" small><v-icon size="18">mdi-chart-bar</v-icon></v-btn>
              <v-btn icon value="table" small><v-icon size="18">mdi-table</v-icon></v-btn>
            </v-btn-toggle>
            <v-btn icon small color="grey lighten-1" @click="openFullscreen('Verkaufte Zusatzpakete', packagesChartOptions, packagesSeries, 'bar')">
              <v-icon>mdi-fullscreen</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-6">
            <apexchart
              v-if="viewModes.packages === 'chart'"
              type="bar"
              height="400"
              :options="packagesChartOptions"
              :series="packagesSeries"></apexchart>
            <v-data-table
              v-else
              dense
              :items-per-page="-1"
              :headers="tableDataFor.packages.headers"
              :items="tableDataFor.packages.items"
              class="elevation-0 transparent neutral-table"
              dark></v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Zusatzpakete Bestand -->
      <v-col cols="12">
        <v-card v-if="latestPackageStats && latestPackageStats.date" class="rounded-xl dashboard-card elevation-4" hover dark>
          <v-card-title class="px-6 border-bottom">
            <span class="text-h6 font-weight-bold">Aktueller Bestand Zusatzpakete</span>
            <v-spacer></v-spacer>
            <span v-if="latestPackageStats.date" class="text-caption grey--text"
              >Stand: {{ moment(latestPackageStats.date).format("DD.MM.YYYY") }}</span
            >
          </v-card-title>
          <v-card-text class="pt-6">
            <v-data-table
              dense
              :items-per-page="-1"
              :headers="latestPackagesHeaders"
              :items="latestPackagesItems"
              class="elevation-0 transparent neutral-table"
              dark
              hide-default-footer>
              <template v-slot:body.append>
                <tr class="font-weight-bold" style="background: rgba(255, 255, 255, 0.05)">
                  <td>Total</td>
                  <td v-for="header in latestPackagesHeaders.slice(1, -1)" :key="header.value" class="text-right">
                    {{ latestPackagesTotals[header.value] }}
                  </td>
                  <td class="text-right white--text">
                    {{ latestPackagesOverallTotal }}
                  </td>
                </tr>
              </template>
              <template v-slot:item="{ item }">
                <tr>
                  <td class="font-weight-medium">{{ item.Studio }}</td>
                  <td v-for="header in latestPackagesHeaders.slice(1, -1)" :key="header.value" class="text-right grey--text text--lighten-1">
                    {{ item[header.value] }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ item.Total }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- GF SECTION -->
      <v-col cols="12" class="mt-10 mb-4">
        <div class="d-flex align-center">
          <div class="text-h5 font-weight-bold mr-4 white--text">Group Fitness</div>
          <v-divider class="grey darken-3"></v-divider>
        </div>
      </v-col>

      <v-col cols="12">
        <v-card class="rounded-xl dashboard-card elevation-4" hover dark>
          <v-card-title class="d-flex align-center px-6 border-bottom">
            <div>
              <div class="text-caption grey--text">{{ gfMetricDescription }}</div>
            </div>
            <v-spacer></v-spacer>

            <!-- Metric Toggle -->
            <v-btn-toggle v-model="viewModes.gfMetric" mandatory dense borderless background-color="rgba(255,255,255,0.05)" class="rounded-lg mr-2">
              <v-btn value="performance" small class="px-4"><v-icon left small>mdi-run</v-icon>Performance</v-btn>
              <v-btn value="participation" small class="px-4"><v-icon left small>mdi-account-group</v-icon>Mitglieder %</v-btn>
            </v-btn-toggle>

            <!-- Period Toggle (Only for Participation) -->
            <v-btn-toggle
              v-if="viewModes.gfMetric === 'participation'"
              v-model="gfPeriod"
              mandatory
              dense
              borderless
              background-color="rgba(255,255,255,0.05)"
              class="rounded-lg mr-2">
              <v-btn value="yearly" small>Jahr</v-btn>
              <v-btn value="monthly" small>Monat</v-btn>
            </v-btn-toggle>

            <!-- Chart/Table Toggle -->
            <v-btn-toggle v-model="viewModes.gfStats" mandatory dense borderless background-color="rgba(255,255,255,0.05)" class="rounded-lg mr-2">
              <v-btn icon value="chart" small><v-icon size="18">mdi-chart-bar</v-icon></v-btn>
              <v-btn icon value="table" small><v-icon size="18">mdi-table</v-icon></v-btn>
            </v-btn-toggle>

            <v-btn icon small color="grey lighten-1" @click="openFullscreen('Group Fitness Statistiken', gfChartOptions, gfChartSeries, 'bar')">
              <v-icon>mdi-fullscreen</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-6">
            <apexchart v-if="viewModes.gfStats === 'chart'" type="bar" height="600" :options="gfChartOptions" :series="gfChartSeries"></apexchart>
            <v-data-table
              v-else
              dense
              :items-per-page="-1"
              :headers="[{ text: 'Studio', value: 'name' }, ...gfDynamicColumns.map((c) => ({ text: c.label, value: c.key, align: 'right' }))]"
              :items="gfSortedData"
              class="elevation-0 transparent neutral-table"
              dark></v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </div>
  </v-card>
</template>

<script>
import api from "@/api"
import moment from "moment"

export default {
  name: "growthStatistics",
  props: {
    studios: { type: Array, default: () => [] },
  },
  data() {
    return {
      today: new Date().toISOString().substr(0, 10),
      startDate: new Date("2025-01-01").toISOString().substr(0, 10),
      endDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      menuStart: false,
      menuEnd: false,
      contractStatistics: {
        studioNames: [],
        newContractsSeries: [],
        renewalsSeries: [],
        upgradesSeries: [],
        packagesSeries: [],
      },
      latestPackageStats: { date: null, stats: [] },

      // Loading States
      loading: false,
      loadingGF: false,
      gfLoaded: false,
      loaded: false,

      growthData: {},

      // GF Data (Dynamic)
      gfPerformanceData: {}, // { Studio: { 2023: x, 2024: y } }
      gfParticipationData: {}, // { Studio: { "2024-08": x, "2024-09": y } }

      gfPeriod: "yearly", // 'yearly' or 'monthly'

      showCustomerInfosTable: false,

      // Dialog States
      showContractsDialog: false,
      showRankingDialog: false,

      viewModes: {
        customersPerQm: "chart",
        customersPer100: "chart",
        newContracts: "chart",
        renewals: "chart",
        upgrades: "chart",
        packages: "chart",
        gfStats: "chart",
        gfMetric: "performance", // 'performance' or 'participation'
      },
      loadingMessages: ["Lade Daten...", "Analysiere Statistiken...", "Bereite Ansichten vor..."],
      loadingMessage: "Bitte warten...",
      loadingInterval: null,

      // Fullscreen Logic
      fullscreenChart: {
        dialog: false,
        title: "",
        options: {},
        series: [],
        type: "bar",
      },
    }
  },
  computed: {
    moment() {
      return moment
    },
    formattedStartDate() {
      return moment(this.startDate).format("DD.MM.YYYY")
    },
    formattedEndDate() {
      return moment(this.endDate).format("DD.MM.YYYY")
    },
    formattedDateRange() {
      return `Zeitraum: ${this.formattedStartDate} - ${this.formattedEndDate}`
    },
    // --- List Helpers for Modals ---
    newContractsDetailedList() {
      if (!this.contractStatistics || !this.contractStatistics.newContractsSeries) return []
      const data = this.contractStatistics.newContractsSeries[0]?.data || []
      const names = this.contractStatistics.studioNames || []

      return names
        .map((name, index) => ({
          name,
          value: data[index] || 0,
        }))
        .sort((a, b) => b.value - a.value)
    },
    rankingList() {
      if (!this.growthData.studios) return []
      return this.growthData.studios
        .filter((s) => s.studioName !== "Gesamt")
        .sort((a, b) => parseFloat(b.growthPercentage) - parseFloat(a.growthPercentage))
    },
    // --- Summary Stats for Header Cards ---
    summaryStats() {
      if (!this.contractStatistics || !this.contractStatistics.newContractsSeries || !this.growthData.timeline) {
        return { totalNewContracts: 0, growthPercentage: 0, totalPackages: 0, topStudio: { name: "-", growth: 0 } }
      }

      // Total New Contracts
      const newContractsData = this.contractStatistics.newContractsSeries[0]?.data || []
      const totalNewContracts = newContractsData.reduce((a, b) => a + b, 0)

      // Growth % (Overall)
      const lastTimeline = this.growthData.timeline[this.growthData.timeline.length - 1]
      let growthPercentage = 0
      if (lastTimeline && lastTimeline.goal > 0) {
        growthPercentage = Math.round((lastTimeline.actual / lastTimeline.goal) * 100)
      }

      // Total Packages
      let totalPackages = 0
      if (this.contractStatistics.packagesSeries) {
        this.contractStatistics.packagesSeries.forEach((s) => {
          totalPackages += s.data.reduce((a, b) => a + b, 0)
        })
      }

      // Top Studio
      let topStudio = { name: "-", growth: 0 }
      let maxGrowth = -Infinity
      if (this.growthData.studios) {
        this.growthData.studios.forEach((s) => {
          if (s.studioName !== "Gesamt" && parseFloat(s.growthPercentage) > maxGrowth) {
            maxGrowth = parseFloat(s.growthPercentage)
            topStudio = { name: s.studioName, growth: s.growthPercentage }
          }
        })
      }

      return {
        totalNewContracts,
        growthPercentage,
        totalPackages,
        topStudio,
      }
    },
    // --- CHART THEME OPTIONS ---
    commonChartOptions() {
      return {
        chart: { foreColor: "#B0B0B0", toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
        grid: { borderColor: "#333", strokeDashArray: 4 },
        tooltip: { theme: "dark", style: { fontSize: "13px" }, cssClass: "modern-tooltip" },
        dataLabels: { enabled: false },
        states: { hover: { filter: { type: "lighten", value: 0.05 } } },
      }
    },
    // --- GF Computed (DYNAMIC) ---
    gfSortedData() {
      const metric = this.viewModes.gfMetric // 'performance' or 'participation'
      const period = this.gfPeriod // 'yearly' or 'monthly'

      // Determine Source Data
      const source = metric === "performance" ? this.gfPerformanceData : this.gfParticipationData
      if (!source) return []

      // Helper to calculate average of an array
      const average = (arr) => (arr && arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0)

      // Transform to Array for Charting
      const rows = Object.keys(source).map((studioName) => {
        const dataObj = source[studioName]
        const row = { name: studioName }

        if (metric === "performance") {
          // Alle Jahre aus den Daten einlesen
          Object.keys(dataObj).forEach((y) => {
            row[`y${y}`] = dataObj[y] || 0
          })
          // Sortierwert = neuestes Jahr
          const years = Object.keys(dataObj).map(Number)
          const latestYear = years.length ? Math.max(...years) : 0
          row.sortVal = dataObj[latestYear] || 0
        } else {
          // Participation Data (Monthly Keys "YYYY-MM")
          if (period === "yearly") {
            // Alle Jahre aus den Monats-Keys extrahieren ("YYYY-MM" -> "YYYY")
            const yearsInData = new Set()
            Object.keys(dataObj).forEach((k) => yearsInData.add(k.substring(0, 4)))

            yearsInData.forEach((year) => {
              const vals = Object.keys(dataObj)
                .filter((k) => k.startsWith(year))
                .map((k) => dataObj[k])
              row[`y${year}`] = parseFloat(average(vals).toFixed(2))
            })

            // Sortierwert = neuestes Jahr
            const sortedYears = Array.from(yearsInData).sort()
            const latestYear = sortedYears[sortedYears.length - 1]
            row.sortVal = latestYear ? row[`y${latestYear}`] : 0
          } else {
            // Monthly (Latest 3 months for column chart? Or sparklines? Or just specific months?)
            // Let's pick the last 3 available months across all studios to align columns
            // For simplicity, let's just map all available months to keys like "m_YYYY_MM"
            Object.keys(dataObj).forEach((k) => {
              row[`m_${k}`] = dataObj[k]
            })
            // Sort by the latest available month
            const latestKey = Object.keys(dataObj).sort().pop()
            row.sortVal = latestKey ? dataObj[latestKey] : 0
          }
        }
        return row
      })

      return rows.sort((a, b) => b.sortVal - a.sortVal)
    },
    gfMetricDescription() {
      if (this.viewModes.gfMetric === "performance") {
        const years = new Set()
        Object.values(this.gfPerformanceData || {}).forEach((s) => {
          Object.keys(s).forEach((y) => years.add(y))
        })
        const yearsStr = Array.from(years)
          .sort()
          .map((y) => y.toString().slice(-2))
          .join("/")
        return `Durchschnittliche Teilnehmer pro Kurs ${yearsStr} (1.1 - 30.11.)`
      } else {
        const years = new Set()
        Object.values(this.gfParticipationData || {}).forEach((s) => {
          Object.keys(s).forEach((k) => years.add(k.substring(0, 4)))
        })
        const yearsStr = Array.from(years)
          .sort()
          .map((y) => y.slice(-2))
          .join("/")
        return `Anteil der Mitglieder mit mind. 1 Besuch pro Monat ${yearsStr} (1.8 - 30.11.)`
      }
    },
    gfDynamicColumns() {
      // Determines which columns/years to show based on mode
      const metric = this.viewModes.gfMetric
      const period = this.gfPeriod

      if (metric === "performance") {
        // Alle verfügbaren Jahre sammeln
        const allYears = new Set()
        Object.values(this.gfPerformanceData || {}).forEach((s) => {
          Object.keys(s).forEach((y) => allYears.add(parseInt(y)))
        })
        // Absteigend sortieren -> neuestes Jahr zuerst
        const sortedYears = Array.from(allYears).sort((a, b) => b - a)

        // Farbpalette: Akzent für neuestes Jahr, danach gräulich
        const colors = ["#536DFE", "#B0B0B0", "#E5E7EB", "#90A4AE", "#78909C", "#607D8B"]

        return sortedYears.map((year, i) => ({
          key: `y${year}`,
          label: `Ø Teilnehmer ${year}`,
          color: colors[i] || "#B0B0B0",
        }))
      } else {
        // Participation
        if (period === "yearly") {
          // Alle Jahre aus den Monats-Keys sammeln
          const allYears = new Set()
          Object.values(this.gfParticipationData || {}).forEach((s) => {
            Object.keys(s).forEach((k) => allYears.add(k.substring(0, 4)))
          })
          // Absteigend: neuestes Jahr zuerst
          const sortedYears = Array.from(allYears).sort((a, b) => b.localeCompare(a))

          const colors = ["#536DFE", "#B0B0B0", "#E5E7EB", "#90A4AE", "#78909C", "#607D8B"]

          return sortedYears.map((year, i) => ({
            key: `y${year}`,
            label: `Ø Beteiligung ${year} (%)`,
            color: colors[i] || "#B0B0B0",
          }))
        } else {
          // Monthly - Dynamic Keys from Data
          // Collect all unique month keys from source
          const source = this.gfParticipationData
          const allKeys = new Set()
          Object.values(source).forEach((s) => Object.keys(s).forEach((k) => allKeys.add(k)))
          // Sort and take last 6 months to avoid overcrowding
          const sortedKeys = Array.from(allKeys).sort().slice(-6)

          // Generate distinct colors or shades
          const colors = ["#E5E7EB", "#CFD8DC", "#B0BEC5", "#90A4AE", "#78909C", "#536DFE"] // Light to Dark/Blue

          return sortedKeys.map((k, i) => ({
            key: `m_${k}`,
            label: moment(k).format("MMM YY") + " (%)",
            color: colors[i] || "#B0B0B0",
          }))
        }
      }
    },
    gfChartSeries() {
      return this.gfDynamicColumns.map((col) => ({
        name: col.label,
        data: this.gfSortedData.map((d) => d[col.key] || 0),
      }))
    },
    gfChartOptions() {
      return {
        ...this.commonChartOptions,
        chart: {
          ...this.commonChartOptions.chart,
          id: "gf-stats-chart",
          stacked: false,
          type: "bar",
          fontFamily: "Roboto, sans-serif",
        },
        plotOptions: {
          bar: {
            horizontal: false, // Vertical bars
            borderRadius: 4,
            columnWidth: "60%",
            dataLabels: {
              position: "top", // Outside on top
              orientation: "vertical", // Vertical text
            },
          },
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ["#ffffff"], // White text against dark background
            fontSize: "12px",
            fontWeight: "bold",
          },
          offsetY: -40, // Slightly above the bar
          formatter: function (val) {
            return val > 0 ? val : ""
          },
          background: { enabled: true, foreColor: "#000", opacity: 0.6, borderRadius: 2 }, // Dark background for contrast
        },
        xaxis: {
          categories: this.gfSortedData.map((d) => d.name),
          labels: {
            style: {
              colors: "#E0E0E0",
              fontSize: "12px",
              fontWeight: 600,
            },
            rotate: -45,
            trim: false,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: {
              colors: "#FFFFFF",
              fontSize: "14px",
              fontWeight: 700,
            },
          },
        },
        grid: {
          borderColor: "#404040",
          yaxis: {
            lines: {
              show: true,
            },
          },
          xaxis: {
            lines: {
              show: false,
            },
          },
        },
        colors: this.gfDynamicColumns.map((c) => c.color),
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: "16px",
          fontWeight: "bold",
          labels: { colors: "#FFFFFF" },
          itemMargin: { horizontal: 15, vertical: 5 },
        },
        tooltip: {
          theme: "dark",
          style: { fontSize: "16px" },
        },
      }
    },

    // --- Existing Computed ---
    sortedContractData() {
      const stats = this.contractStatistics
      if (!stats || !stats.studioNames || !stats.studioNames.length) return {}

      const sortable = stats.studioNames.map((name, index) => {
        const totalPackages = (stats.packagesSeries || []).reduce((sum, series) => sum + (series.data[index] || 0), 0)
        return {
          name,
          newContracts: stats.newContractsSeries[0]?.data[index] || 0,
          renewals: stats.renewalsSeries[0]?.data[index] || 0,
          upgrades: stats.upgradesSeries[0]?.data[index] || 0,
          totalPackages,
          packages: (stats.packagesSeries || []).map((series) => ({ name: series.name, data: series.data[index] })),
        }
      })

      const createSortedData = (key) => {
        if (!sortable.length) return { names: [], seriesData: [], tableData: [] }
        const sorted = [...sortable].sort((a, b) => b[key] - a[key])
        return {
          names: sorted.map((s) => s.name),
          seriesData: sorted.map((s) => s[key]),
          tableData: sorted.map((s) => ({ name: s.name, value: s[key] })),
        }
      }

      const packagesSorted = [...sortable].sort((a, b) => b.totalPackages - a.totalPackages)

      return {
        newContracts: createSortedData("newContracts"),
        renewals: createSortedData("renewals"),
        upgrades: createSortedData("upgrades"),
        packages: {
          names: packagesSorted.map((s) => s.name),
          series: (stats.packagesSeries || []).map((series) => ({
            name: series.name,
            data: packagesSorted.map((s) => (s.packages.find((p) => p.name === series.name) || {}).data || 0),
          })),
        },
      }
    },
    tableDataFor() {
      const stats = this.contractStatistics
      const growthStats = this.chartData
      if (!stats || !stats.studioNames || !this.sortedContractData.packages)
        return { packages: { headers: [], items: [] }, customersPerQm: { headers: [], items: [] }, customersPer100: { headers: [], items: [] } }

      const packagesHeaders = [
        { text: "Studio", value: "name", sortable: true },
        ...(stats.packagesSeries || []).map((s) => ({ text: s.name, value: s.name, sortable: true })),
      ]

      const packagesItems = (this.sortedContractData.packages?.names || []).map((name) => {
        const row = { name }
        const studioIndex = stats.studioNames.indexOf(name)
        ;(stats.packagesSeries || []).forEach((series) => {
          row[series.name] = series.data[studioIndex] || 0
        })
        return row
      })

      return {
        newContracts: this.sortedContractData.newContracts?.tableData || [],
        renewals: this.sortedContractData.renewals?.tableData || [],
        upgrades: this.sortedContractData.upgrades?.tableData || [],
        packages: { headers: packagesHeaders, items: packagesItems },
        customersPerQm: {
          headers: [
            { text: "Studio", value: "studioName" },
            { text: "Start", value: "startCustomersPerQm" },
            { text: "Ende", value: "endCustomersPerQm" },
          ],
          items: [...growthStats].sort((a, b) => parseFloat(b.endCustomersPerQm) - parseFloat(a.endCustomersPerQm)),
        },
        customersPer100: {
          headers: [
            { text: "Studio", value: "studioName" },
            { text: "Start", value: "startCustomersPer100" },
            { text: "Ende", value: "endCustomersPer100" },
          ],
          items: [...growthStats].sort((a, b) => parseFloat(b.endCustomersPer100) - parseFloat(a.endCustomersPer100)),
        },
      }
    },
    newContractsSeries() {
      return [{ name: "Neuverträge", data: this.sortedContractData.newContracts?.seriesData || [] }]
    },
    newContractsChartOptions() {
      return {
        ...this.commonChartOptions,
        chart: { ...this.commonChartOptions.chart, type: "bar" },
        xaxis: { categories: this.sortedContractData.newContracts?.names || [], axisBorder: { show: false }, axisTicks: { show: false } },
        colors: ["#00b894"], // Mint Green
        plotOptions: { bar: { horizontal: false, borderRadius: 3 } },
      }
    },
    renewalsSeries() {
      return [{ name: "Erneuerungen", data: this.sortedContractData.renewals?.seriesData || [] }]
    },
    renewalsChartOptions() {
      return {
        ...this.commonChartOptions,
        chart: { ...this.commonChartOptions.chart, type: "bar" },
        xaxis: { categories: this.sortedContractData.renewals?.names || [], axisBorder: { show: false }, axisTicks: { show: false } },
        colors: ["#0984e3"], // Blue
        plotOptions: { bar: { horizontal: false, borderRadius: 3 } },
      }
    },
    upgradesSeries() {
      return [{ name: "Upgrades", data: this.sortedContractData.upgrades?.seriesData || [] }]
    },
    upgradesChartOptions() {
      return {
        ...this.commonChartOptions,
        chart: { ...this.commonChartOptions.chart, type: "bar" },
        xaxis: { categories: this.sortedContractData.upgrades?.names || [], axisBorder: { show: false }, axisTicks: { show: false } },
        colors: ["#fd79a8"], // Pink
        plotOptions: { bar: { horizontal: false, borderRadius: 3 } },
      }
    },
    packagesSeries() {
      return this.sortedContractData.packages?.series || []
    },
    packagesChartOptions() {
      return {
        ...this.commonChartOptions,
        chart: { ...this.commonChartOptions.chart, type: "bar", stacked: true },
        xaxis: { categories: this.sortedContractData.packages?.names || [], axisBorder: { show: false }, axisTicks: { show: false } },
        plotOptions: { bar: { horizontal: false, borderRadius: 2 } },
        legend: {
          position: "top",
          horizontalAlign: "left",
          formatter: (s) => (s.length > 20 ? s.substring(0, 20) + "..." : s),
          labels: { colors: "#B0B0B0" },
        },
        colors: ["#0984e3", "#00b894", "#fdcb6e", "#d63031", "#6c5ce7", "#00cec9", "#e17055", "#2d3436"],
      }
    },
    headers() {
      let baseHeaders = [
        { text: "Studio", value: "studioName" },
        { text: "Kunden (Start)", value: "startCustomers" },
        { text: "Kunden (Ende)", value: "endCustomers" },
        { text: "Wachstum (%)", value: "growthPercentage" },
        { text: "Ziel 2025", value: "goal2025" },
        { text: "Wachstum bis Ziel (%)", value: "growthToGoalPercentage" },
      ]
      if (this.showCustomerInfosTable) {
        baseHeaders.splice(
          4,
          0,
          { text: "Kunden/QM (Start)", value: "startCustomersPerQm" },
          { text: "Kunden/QM (Ende)", value: "endCustomersPerQm" },
          { text: "Kunden/100% (Start)", value: "startCustomersPer100" },
          { text: "Kunden/100% (Ende)", value: "endCustomersPer100" },
        )
      }
      return baseHeaders
    },
    chartData() {
      return (this.growthData.studios || []).filter((d) => d.studioName !== "Gesamt")
    },
    growthChartOptions() {
      return {
        ...this.commonChartOptions,
        chart: { ...this.commonChartOptions.chart, id: "growth-chart", type: "line" },
        xaxis: {
          type: "datetime",
          categories: (this.growthData.timeline || []).map((d) => d.date),
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        colors: ["#636e72", "#00b894"], // Goal: Grey, Actual: Green
        stroke: { width: [2, 4], curve: "smooth", dashArray: [5, 0] },
        tooltip: { theme: "dark", x: { format: "dd.MM.yyyy" } },
        fill: {
          type: "gradient",
          gradient: {
            shade: "dark",
            gradientToColors: ["#00b894"],
            shadeIntensity: 1,
            type: "horizontal",
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100, 100, 100],
          },
        },
      }
    },
    growthSeries() {
      return [
        { name: "Ziel", data: (this.growthData.timeline || []).map((d) => d.goal) },
        { name: "Erreicht", data: (this.growthData.timeline || []).map((d) => d.actual) },
      ]
    },
    customersPerQmChartOptions() {
      const sortedData = [...this.chartData].sort((a, b) => parseFloat(b.endCustomersPerQm) - parseFloat(a.endCustomersPerQm))
      return {
        ...this.commonChartOptions,
        chart: { ...this.commonChartOptions.chart, id: "customer-per-qm-chart" },
        xaxis: { categories: sortedData.map((d) => d.studioName), axisBorder: { show: false }, axisTicks: { show: false } },
        plotOptions: { bar: { dataLabels: { position: "top" }, borderRadius: 3 } },
        dataLabels: { enabled: true, offsetY: -20, style: { fontSize: "11px", colors: ["#e2e8f0"] } },
        colors: ["#6c5ce7", "#74b9ff"],
      }
    },
    customersPerQmSeries() {
      const sortedData = [...this.chartData].sort((a, b) => parseFloat(b.endCustomersPerQm) - parseFloat(a.endCustomersPerQm))
      return [
        { name: "Kunden/QM (Start)", data: sortedData.map((d) => d.startCustomersPerQm) },
        { name: "Kunden/QM (Ende)", data: sortedData.map((d) => d.endCustomersPerQm) },
      ]
    },
    customersPer100ChartOptions() {
      const sortedData = [...this.chartData].sort((a, b) => parseFloat(b.endCustomersPer100) - parseFloat(a.endCustomersPer100))
      return {
        ...this.commonChartOptions,
        chart: { ...this.commonChartOptions.chart, id: "customer-per-100-chart" },
        xaxis: { categories: sortedData.map((d) => d.studioName), axisBorder: { show: false }, axisTicks: { show: false } },
        plotOptions: { bar: { dataLabels: { position: "top" }, borderRadius: 3 } },
        dataLabels: { enabled: true, offsetY: -20, style: { fontSize: "11px", colors: ["#e2e8f0"] } },
        colors: ["#6c5ce7", "#00b894"],
      }
    },
    customersPer100Series() {
      const sortedData = [...this.chartData].sort((a, b) => parseFloat(b.endCustomersPer100) - parseFloat(a.endCustomersPer100))
      return [
        { name: "Kunden/100% (Start)", data: sortedData.map((d) => d.startCustomersPer100) },
        { name: "Kunden/100% (Ende)", data: sortedData.map((d) => d.endCustomersPer100) },
      ]
    },
    tableData() {
      return this.growthData.studios || []
    },
    latestPackagesAllNames() {
      if (!this.latestPackageStats.stats) return []
      const packageSet = new Set()
      this.latestPackageStats.stats.forEach((studio) => {
        studio.Packages.forEach((pkg) => packageSet.add(pkg.NAME))
      })
      return Array.from(packageSet).sort()
    },
    latestPackagesHeaders() {
      return [
        { text: "Studio", value: "Studio", align: "start" },
        ...this.latestPackagesAllNames.map((name) => ({ text: name, value: name, align: "end" })),
        { text: "Total", value: "Total", align: "end" },
      ]
    },
    latestPackagesItems() {
      if (!this.latestPackageStats.stats) return []
      return this.latestPackageStats.stats
        .map((studio) => {
          const row = { Studio: studio.Studio }
          let total = 0
          this.latestPackagesAllNames.forEach((pkgName) => {
            const pkg = studio.Packages.find((p) => p.NAME === pkgName)
            const count = pkg ? pkg.TotalCount : 0
            row[pkgName] = count
            total += count
          })
          row.Total = total
          return row
        })
        .sort((a, b) => b.Total - a.Total)
    },
    latestPackagesTotals() {
      const totals = {}
      this.latestPackagesAllNames.forEach((name) => {
        totals[name] = this.latestPackagesItems.reduce((sum, item) => sum + (item[name] || 0), 0)
      })
      return totals
    },
    latestPackagesOverallTotal() {
      return this.latestPackagesItems.reduce((sum, item) => sum + item.Total, 0)
    },
  },
  methods: {
    openFullscreen(title, options, series, type = "bar") {
      this.fullscreenChart.title = title
      // Deep copy but careful with reactivity. Using Object spread for better compatibility if needed,
      // but JSON parse/stringify is safer for detachment.
      this.fullscreenChart.options = {
        ...JSON.parse(JSON.stringify(options)),
        chart: {
          ...options.chart,
          height: "100%", // Force height in fullscreen
        },
      }
      this.fullscreenChart.series = series
      this.fullscreenChart.type = type
      this.fullscreenChart.dialog = true
    },
    fetchData() {
      this.fetchGrowthData()
      // this.gfLoaded = false // Reset GF state on main reload
    },
    async fetchGrowthData() {
      this.loading = true

      // --- ⚙️ LADE-NACHRICHTEN STARTEN ⚙️ ---
      let messageIndex = 0
      this.loadingMessage = this.loadingMessages[messageIndex]
      this.loadingInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % this.loadingMessages.length
        this.loadingMessage = this.loadingMessages[messageIndex]
      }, 2000)

      try {
        const [growthResponse, contractResponse, latestPackagesResponse, gfPerfResponse, gfPartResponse] = await Promise.all([
          api.fetchGrowthStatistics({ startDate: this.startDate, endDate: this.endDate, studios: this.studios.map((s) => s._id) }),
          api.fetchContractStatistics({ startDate: this.startDate, endDate: this.endDate, studios: this.studios.map((s) => s._id) }),
          api.fetchLatestPackageStats(),
          api.fetchGFParticipants(true), // Default to visited=true
          api.fetchGFCustomerAverage(),
        ])
        this.growthData = growthResponse
        this.contractStatistics = contractResponse
        this.latestPackageStats = latestPackagesResponse
        this.gfPerformanceData = gfPerfResponse
        this.gfParticipationData = gfPartResponse

        this.loaded = true
      } catch (error) {
        console.error("Fehler beim Laden der Statistiken:", error)
        this.$toast.error("Fehler beim Laden der Statistiken")
      } finally {
        clearInterval(this.loadingInterval)
        this.loading = false
      }
    },
  },
  created() {
    // this.fetchData()
  },
}
</script>

<style scoped>
.neutral-dashboard {
  font-family: "Roboto", sans-serif;
  background-color: #212121 !important;
}

.dashboard-card {
  background-color: #2e2e2e !important;
  border: none !important;
  transition: all 0.3s ease;
}

.card-hover:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5) !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.cursor-pointer {
  cursor: pointer;
}

/* Gradient Cards - Adjusted for #212121 */
.gradient-card-1 {
  background: linear-gradient(135deg, #00b894 0%, #00cec9 100%) !important;
}
.gradient-card-2 {
  background: linear-gradient(135deg, #0984e3 0%, #74b9ff 100%) !important;
}
.gradient-card-3 {
  background: linear-gradient(135deg, #e17055 0%, #fab1a0 100%) !important;
}
.gradient-card-4 {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%) !important;
}

.neutral-input >>> .v-input__slot {
  background: #2e2e2e !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.neutral-input >>> input {
  color: #e2e8f0 !important;
}

.glow-effect-pink {
  box-shadow: 0 0 15px rgba(245, 0, 87, 0.3);
}

.neutral-table >>> th {
  color: #b0b0b0 !important;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}
.neutral-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}
.transparent-table {
  background: transparent !important;
}
.transparent-table >>> th {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.transparent-table >>> td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

/* Scrollbar custom */
::-webkit-scrollbar {
  width: 10px;
}
::-webkit-scrollbar-track {
  background: #212121;
}
::-webkit-scrollbar-thumb {
  background: #424242;
  border-radius: 5px;
}
::-webkit-scrollbar-thumb:hover {
  background: #616161;
}
</style>
